<template>
  <div ref="preview-image">
    <div
      v-if="!actualVector"
      class="vx-carousel-container w-100 d-flex align-items-center justify-content-center"
    >
      <div class="preview-skeleton-wrapper">
        <div class="preview-picture-skeleton skeleton-box"></div>
        <div class="preview-info-skeleton-container">
          <div class="preview-info-attributes-skeleton-wrapper">
            <div class="preview-info-icons-wrapper-skeleton">
              <div class="preview-info-icon-skeleton skeleton-box"></div>
              <div class="preview-info-icon-skeleton skeleton-box"></div>
              <div class="preview-info-icon-skeleton skeleton-box"></div>
            </div>
            <div class="preview-info-attributes-skeleton skeleton-box"></div>
          </div>
          <UiButton
            :enabled="false"
            class="download-btn download-btn-skeleton"
            min-width="250"
            min-height="45"
            height="45"
            >{{ $t('preview.download.main.download') }}</UiButton
          >
        </div>
      </div>
    </div>
    <div v-else>
      <div
        class="vx-carousel-container preview-actual-vector-rendering-span-wrapper"
        :class="{ 'bg-gray': actualVector.svg }"
      >
        <div
          class="preview-actual-vector-rendering-span"
          :class="{ 'd-none': imageLoaded || actualVector.svg }"
        />

        <div v-if="actualVector.svg" class="justify-content-center align-items-center vx-btns-svg">
          <div v-if="!isDesignActive" class="d-flex merch-tshirt">
            <Tooltip :edit-color-tooltip="true">
              <template #element>
                <a
                  href="https://www.vexels.com/t-shirt-maker"
                  target="_blank"
                  class="icon-shirt"
                  :style="wrapperStyles"
                  @click="clickShirtMaker()"
                >
                  <img
                    src="https://cdn.vexels.com/img/icons/merch_only_white.svg"
                    height="31"
                    width="36"
                    :alt="$t('preview.main.content.merchOnlyIconWhiteAlt')"
                    loading="lazy"
                  />
                </a>
              </template>
              <template #title>
                <p class="tooltip-description">
                  {{ $t('main.preview.editColor.tooltipMerch') }}
                </p>
              </template>
            </Tooltip>
          </div>

          <Tooltip :edit-color-tooltip="true">
            <template #element>
              <button type="button" class="btn p-0 border-l" @click="openEditColor">
                <img
                  src="https://cdn.vexels.com/img/edit-colors.svg"
                  width="36"
                  height="36"
                  :alt="$t('main.preview.editColor.colorsWheelAlt')"
                  loading="lazy"
                />
              </button>
            </template>
            <template #title>
              <p class="tooltip-description">
                {{ $t('main.preview.editColor.tooltipEdit') }}
              </p>
            </template>
          </Tooltip>
        </div>

        <div v-if="actualVector.svg" id="container-preview-svg" class="container position-relative">
          <EditColor
            :vector-image="actualVector.list"
            :vector-height="actualVector.height"
            :vector-width="actualVector.width"
            :show-edit-color="showEditColor"
            :gradients="gradients"
            :colors="colors"
            :alt-text="actualVector.name"
            @load-img-svg="resizeContainer"
          />
          <Overlay :data="actualVector" />
        </div>
        <div v-else id="container-preview-image">
          <picture>
            <source
              v-for="{ resolution, size } in imagesSize"
              :key="resolution"
              :media="`(max-width: ${resolution}px)`"
              :srcset="`${actualVector.image}?w=${size}&fmt=webp`"
            />
            <img
              id="image-preview"
              :src="actualVector.image"
              class="img-fluid"
              :class="{ 'opacity-1': imageLoaded }"
              :alt="actualVector.name"
              no-webp
              height="667"
              :width="(667 * actualVector.width) / actualVector.height"
              loading="lazy"
              @load="imageLoad"
            />
          </picture>
          <Overlay :data="actualVector" />
        </div>
      </div>
      <nav id="preview-bottom" class="preview-bottom d-flex">
        <div class="icons-container d-flex">
          <AttributeIcons v-if="displayIcons" :icons="actualVector.attributesIcons" />
          <VectorDetailsInfo
            :vector-image="actualVector.image"
            :date-added="actualVector.dateAdded"
            :file-type="actualVector.type"
            :licence="actualVector.licence.licenceCode"
          />
          <div v-if="hasStyle" class="vx-styles-container">
            <ul>
              <li class="vx-styles-item">
                <nuxt-link
                  :to="$trailingSlash(`${localePath('styles')}/${style.link}`)"
                  class="vx-styles-image-container"
                  @click.native="onClickStyle()"
                >
                  <img :src="style.thumbnail" :alt="style.name" loading="lazy" />
                  <span class="exp-bg custom-btn">{{
                    $t('preview.bottomBar.styles.seeStyles')
                  }}</span>
                </nuxt-link>
                <p class="vx-styles-name">{{ style.name }}</p>
              </li>
            </ul>
          </div>
        </div>
        <DownloadBtn
          :content-id="parseInt(actualVector.id)"
          :edit-color="showEditColor"
          @show-purchase-flow="$emit('show-purchase-flow')"
        />
      </nav>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import { displayGradients, displayColors, initEditColor } from '~/utils/editColor.js';
import AttributeIcons from '@/components/Vectors/AttributeIcons';
import UiButton from 'UI/Button';
import VectorDetailsInfo from '@/components/Vectors/VectorDetailsInfo';
import DownloadBtn from '@/components/Preview/Download/DownloadBtn';
import Overlay from '@/components/Preview/Overlay';
import Tooltip from '@/components/Common/Tooltip';

const EditColor = () => import('@/components/Preview/EditColor');

export default {
  name: 'PreviewContent',
  components: {
    AttributeIcons,
    DownloadBtn,
    UiButton,
    EditColor,
    Overlay,
    VectorDetailsInfo,
    Tooltip
  },
  data() {
    return {
      imageLoaded: false,
      gradients: [],
      colors: [],
      imagesSize: [
        { resolution: 360, size: 330, minResolution: 0 },
        { resolution: 375, size: 345, minResolution: 361 },
        { resolution: 414, size: 384, minResolution: 376 },
        { resolution: 768, size: 738, minResolution: 415 },
        { resolution: 834, size: 804, minResolution: 769 },
        { resolution: 1024, size: 994, minResolution: 835 },
        { resolution: 1366, size: 1321, minResolution: 1025 },
        { resolution: 1536, size: 1506, minResolution: 1367 },
        { resolution: 1920, size: 1800, minResolution: 1537 }
      ]
    };
  },
  head() {
    let preloadImages = [];
    if (this.actualVector) {
      if (!this.actualVector.isSvg) {
        this.imagesSize.map((item) => {
          preloadImages.push({
            rel: 'preload',
            as: 'image',
            href: `${this.actualVector.image}?w=${item.size}&fmt=webp`,
            media: `(min-width: ${item.minResolution}px) and (max-width: ${item.resolution}px)`,
            type: 'image/webp'
          });
        });
      } else {
        preloadImages.push({ rel: 'preload', as: 'image', href: this.actualVector.image });
      }

      return {
        link: [...preloadImages]
      };
    }
  },
  computed: {
    ...mapGetters('vectors', ['actualVector']),
    ...mapState('vectors', ['actualVector', 'showEditColor']),
    displayIcons() {
      return this.actualVector.attributesIcons?.length;
    },
    hasStyle() {
      return this.actualVector.styles && this.actualVector.styles[0];
    },
    style() {
      return this.actualVector.styles && this.actualVector.styles[0]
        ? this.actualVector.styles[0]
        : null;
    },
    isDesignActive() {
      return this.$store.getters['experience/getExperience'] == 'design';
    },
    wrapperStyles() {
      const color = this.isDesignActive ? '#20B9B5' : '#fb556b';
      return `background-color: ${color}`;
    }
  },
  watch: {
    actualVector() {
      this.imageLoaded = false;
      this.resizeContainer();
    }
  },
  mounted() {
    window.addEventListener('resize', this.resizeContainer);
  },
  destroyed() {
    window.removeEventListener('resize', this.resizeContainer);
  },
  created() {
    this.$store.dispatch('vectors/SET_SHOW_EDIT_COLOR', false);
  },
  methods: {
    imageLoad() {
      this.imageLoaded = true;
      this.resizeContainer();
    },
    resizeOpenEditor() {
      this.resizeContainer();
      let containerSvg = document.getElementById('container-preview-svg');
      containerSvg.style.height = 'auto';
    },
    valuesResize() {
      let navHeight = document.getElementById('preview-nav')?.clientHeight;
      let navBottomHeight = document.getElementById('preview-bottom')?.clientHeight;
      let marginTop = 60;
      let marginBottom = 150;
      let total =
        navHeight && navBottomHeight ? navHeight + navBottomHeight + marginTop + marginBottom : 0;
      return total;
    },
    resizeContainer() {
      try {
        let totalHeight = this.valuesResize();
        if (totalHeight && window.innerWidth > 1199) {
          if (this.actualVector && this.actualVector.svg) {
            let containerPreviewSvg = document.getElementById('container-preview-svg');
            totalHeight += 500;
            containerPreviewSvg.style.height = 'auto';
            containerPreviewSvg.style.minHeight = `calc(100vh - ${totalHeight}px)`;
          } else {
            let containerImage = document.getElementById('container-preview-image');
            let imagePreview = document.getElementById('image-preview');

            containerImage.style.height = 'null';
            containerImage.style.width = 'auto';
            containerImage.style.height = `calc(100vh - ${totalHeight}px)`;
            containerImage.style.width = `${imagePreview.clientWidth}px`;
          }
        } else {
          const containerPreviewImage = document.getElementById('container-preview-image');
          if (!this.actualVector.svg && containerPreviewImage) {
            containerPreviewImage.style.height = 'auto';
            containerPreviewImage.style.width = 'auto';
            containerPreviewImage.style.width = `${
              document.getElementById('image-preview').clientWidth
            }px`;
          }
        }
      } catch (error) {
        console.error('Component Preview Carousel resizeContainer:', error);
      }
    },
    onClickStyle() {
      this.$bvModal.hide('preview-vector-modal');
    },
    async openEditColor() {
      await this.$store.dispatch('vectors/SET_SHOW_EDIT_COLOR', !this.showEditColor);
      this.resizeOpenEditor();
      this.gradients = displayGradients('gradients');
      this.colors = displayColors('colors');
      initEditColor();
    },
    clickShirtMaker() {
      const data = { artId: this.actualVector.id };
      localStorage.setItem('vx-try-editor', JSON.stringify(data));
    }
  }
};
</script>
<style lang="scss" src="../Preview.scss" scoped></style>
<style lang="scss" scoped>
#image-preview {
  opacity: 0;
  transition: opacity 0.1s ease-out;
}
.opacity-1 {
  opacity: 1 !important;
}
.preview-skeleton-wrapper {
  display: flex;
  width: 100%;
  margin-bottom: 32px;
  gap: 32px;
  flex-direction: column;
  align-items: center;
  @include md {
    gap: 16px;
    margin: 8px 0px 12px 0px;
  }
  .preview-picture-skeleton {
    width: 80%;
    height: 560px;
    @include xl2 {
      height: 460px;
    }
    @include xl {
      height: 320px;
    }
    @include lg {
      height: 260px;
    }
    @include md {
      height: 200px;
    }
  }
  .preview-info-skeleton-container {
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    .preview-info-attributes-skeleton-wrapper {
      display: flex;
      gap: 12px;
    }
    .preview-info-attributes-skeleton {
      width: 12vw;
      max-width: 200px;
      height: 32px;
      margin-right: 8px;
      @include md {
        width: 60vw;
      }
    }
    .preview-info-icons-wrapper-skeleton {
      display: flex;
      gap: 8px;
      flex-direction: row;
      justify-content: flex-start;
      width: fit-content;
    }
    .preview-info-icon-skeleton {
      width: 34px;
      height: 32px;
    }
    .download-btn-skeleton {
      @include xl {
        display: none;
      }
    }
  }
}
</style>
<style lang="scss">
#container-preview-image {
  position: relative;
  margin: auto;
  width: auto;
  min-height: 200px;
  @include min-xl {
    height: calc(100vh - 295px);
  }
}
#container-preview-svg {
  min-height: 280px;
  padding-top: 20px;
  padding-bottom: 20px;
  @include xl2 {
    padding-top: 10px;
    padding-bottom: 10px;
  }
}
.vx-styles-image-container {
  display: flex;
  align-items: center;
  position: relative;
  width: 45px;
  height: 45px;
  transition: all 0.5s;
  z-index: 5;
  &:hover {
    img {
      filter: brightness(50%);
    }
  }

  img {
    width: 45px;
    height: 45px;
    object-fit: cover;
    transition: all 0.5s;
    border-radius: 4px;
    border-radius: 50px;
  }

  .custom-btn {
    position: absolute;
    left: 50px;
    bottom: 10px;
    padding: 2.5px 17px;
    background: #fb556b;
    width: max-content;
    border-radius: 15px;
    font-size: 14px;
    color: white;
    opacity: 0;
    transition: all 0.5s;
    @include md {
      opacity: 0;
    }
  }
}

.vx-styles-item {
  transition: margin 0.5s;
  margin-right: 4rem;
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 16px;
  &:hover {
    .vx-styles-name {
      opacity: 0;
      @include md {
        opacity: 1;
      }
    }
  }
}

.vx-styles-name {
  margin: 0;
  margin-right: 10px;
  @include md {
    opacity: 1;
    left: 55px;
    position: absolute;
  }
}

.vx-styles-container {
  display: flex;
  align-items: center;
  border-left: 1px solid #e2e2e2;
  margin-left: 15px;
  @include md {
    padding: 0;
    margin-left: 0;
    border-left: none;
  }
  ul {
    padding-left: 15px;
    list-style: none;
    display: flex;
    @include md {
      padding: 0;
      flex-wrap: wrap;
    }
  }
}

.icons-preview {
  display: flex;
  align-items: center;
}
.vx-download-container {
  margin-left: auto;
}
.preview-bottom {
  padding: 15px 0px;
  @include md {
    flex-flow: column-reverse;
  }
  .icons-container {
    @include md {
      flex-flow: column;
      margin: 15px 0;
    }
  }
}
.container-download-options .download-btn .vexels-button {
  @include min-xl {
    min-width: 250px !important;
  }
}
img.canvas-container {
  display: block;
  object-fit: contain;
}
.canvas-container {
  min-height: 250px;
  max-width: 250px;
  width: 100%;
  margin: auto;
  svg {
    height: 100%;
    width: 100%;
  }
}
.editor-color-open {
  svg {
    path {
      &:hover {
        stroke: black;
        stroke-width: 4px;
      }
    }
  }
}
.icon-shirt {
  width: 36px;
  height: 36px;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  img {
    top: 2px;
    position: relative;
    margin-top: 6px;
  }
}
.vx-btns-svg {
  padding: 20px 16px;
  border-radius: 11px;
  margin-bottom: 30px;
  flex-direction: column;
  display: flex;
  position: absolute;
  left: 20px;
  top: 50%;
  background: #fff;
  transform: translate(0, -50%);
  z-index: 20;
  @include lg {
    padding: 16px 28px;
    left: 50%;
    top: 10px;
    flex-direction: row;
    transform: translate(-50%, 0);
  }
  @include xl2 {
    margin-bottom: 5px;
  }
  background: #fff;
  .merch-tshirt {
    border-bottom: 1px solid #e2e2e2;
    padding: 0 0 10px;
    margin: 0 0 10px;
    @include lg {
      border-bottom: none;
      border-right: 1px solid #e2e2e2;
      padding: 0 15px 0 0;
      margin: 0 15px 0 0;
    }
    @include sm {
      padding: 0 10px 0 0;
      margin: 0 10px 0 0;
    }
  }
}
.bg-gray {
  background-color: #efefef;
}
.preview-actual-vector-rendering-span-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  .preview-actual-vector-rendering-span {
    position: absolute;
    width: calc(calc(90vw - 50px) * 0.8);
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 4px;
    background-color: #ebebeb;
    height: 100%;
  }
}
.vx-carousel-container {
  img {
    display: block;
    margin: auto;
    max-height: 100%;
    max-width: 100%;
    width: auto;
    @include xl {
      max-height: 60vh;
    }
  }
  .carousel {
    padding-bottom: 30px;
    position: relative;
    width: max-content;
    max-width: 100%;
    .carousel-inner {
      border-radius: 5px;
      height: 100%;
      width: max-content;
      max-width: 100%;
      .carousel-item {
        height: 100%;
        width: auto;
        max-width: 100%;
        img {
          height: 60vh;
          width: auto !important;
        }
      }
    }
  }
  .carousel-control-next {
    width: auto;
    background-color: rgba(142, 142, 142, 0.5);
    padding: 10px;
    height: 50px;
    top: calc(50% - 25px);
  }
  .carousel-control-prev {
    width: auto;
    background-color: rgba(142, 142, 142, 0.5);
    padding: 10px;
    height: 50px;
    top: calc(50% - 25px);
  }
  .carousel-indicators {
    li {
      width: 10px;
      height: 10px;
      border-radius: 200%;
      background-color: #bba6a6;
    }
    .active {
      background-color: #ff5a5f;
    }
  }
}

@media all and (min-width: 768px) {
  .vx-styles-item {
    &:hover {
      .vx-styles-image-container {
        width: 60px;
        transition: width 0.5s;
      }
      .custom-btn {
        opacity: 1;
        transition: all 0.5s;
        left: 60px;
      }
    }
  }
}

@media all and (max-width: 767px) {
  .vx-styles-item {
    display: flex;
    align-items: center;
    margin: 10px 0;
    width: 50%;
  }
  .vx-styles-image-container {
    position: initial;
    margin: 0;
    width: 30%;
  }
}
</style>
